import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// When Keycloak access token expires the API returns 401.
// Clear local session so the user is sent back to the login page.
// Skip the redirect for the login endpoint itself (wrong password also returns 401).
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const isLoginRequest = error.config?.url?.includes('/auth/login');
    if (error.response?.status === 401 && !isLoginRequest) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const authService = {
  login: async (username: string, password: string) => {
    const response = await api.post('/auth/login', { username, password });
    return response.data;
  },
  forgotPassword: async (email: string) => {
    const response = await api.post('/auth/forgot-password', JSON.stringify(email), {
      headers: { 'Content-Type': 'application/json' }
    });
    return response.data;
  },
  resetPassword: async (token: string, newPassword: string) => {
    const response = await api.post('/auth/reset-password', { token, newPassword });
    return response.data;
  },
};

export const clientService = {
  getClients: async () => {
    const response = await api.get('/clients');
    return response.data;
  },
  getClientBySubdomain: async (subdomain: string) => {
    const response = await api.get(`/clients/subdomain/${subdomain}`);
    return response.data;
  },
  createClient: async (clientData: any) => {
    const response = await api.post('/clients', clientData);
    return response.data;
  },
  updateClient: async (id: number, clientData: any) => {
    const response = await api.put(`/clients/${id}`, clientData);
    return response.data;
  },
  deleteClient: async (id: number) => {
    const response = await api.delete(`/clients/${id}`);
    return response.data;
  },
  uploadLogo: async (id: number, logoFile: File) => {
    const formData = new FormData();
    formData.append('logoFile', logoFile);
    const response = await api.post(`/clients/${id}/upload-logo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },
  updateBranding: async (id: number, branding: { primaryColor?: string; secondaryColor?: string; logoUrl?: string }) => {
    const response = await api.put(`/clients/${id}/branding`, branding);
    return response.data;
  },
};

export const employeeDetailService = {
  getEmployeeDetail: async (userId: number) => {
    const response = await api.get(`/employeedetails/${userId}`);
    return response.data;
  },
  getAllEmployeeDetails: async () => {
    const response = await api.get('/employeedetails');
    return response.data;
  },
  saveEmployeeDetail: async (formData: FormData) => {
    const response = await api.post('/employeedetails', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },
  verifyDocument: async (data: { userId: number; documentType: string; status: string; rejectionReason?: string | null }) => {
    const response = await api.post('/employeedetails/verify-document', data);
    return response.data;
  },
};

export const userService = {
  getUsers: async () => {
    const response = await api.get('/users');
    return response.data;
  },
  getDevelopers: async () => {
    const response = await api.get('/users/developers');
    return response.data;
  },
  getEmployees: async () => {
    const response = await api.get('/users/employees');
    return response.data;
  },
  createUser: async (userData: any) => {
    const response = await api.post('/users', userData);
    return response.data;
  },
  updateUser: async (id: number, userData: any) => {
    const response = await api.put(`/users/${id}`, userData);
    return response.data;
  },
  deleteUser: async (id: number) => {
    const response = await api.delete(`/users/${id}`);
    return response.data;
  },
  resetPassword: async (id: number, newPassword?: string) => {
    const response = await api.post(`/users/${id}/reset-password`, { newPassword: newPassword || '' });
    return response.data;
  },
  changePassword: async (currentPassword: string, newPassword: string) => {
    const response = await api.post('/users/change-password', { currentPassword, newPassword });
    return response.data;
  },
  updateSalary: async (id: number, salary: number) => {
    const response = await api.put(`/users/${id}/salary`, salary);
    return response.data;
  },
  getMyPermissions: async (): Promise<string[]> => {
    const response = await api.get('/users/my-permissions');
    return response.data;
  },
};

export const projectService = {
  getProjects: async () => {
    const response = await api.get('/projects');
    return response.data;
  },
  getUserProjects: async (userId: number) => {
    const response = await api.get(`/projects/user/${userId}`);
    return response.data;
  },
  createProject: async (projectData: any) => {
    const response = await api.post('/projects', projectData);
    return response.data;
  },
  updateProject: async (id: number, projectData: any) => {
    const response = await api.put(`/projects/${id}`, projectData);
    return response.data;
  },
  deleteProject: async (id: number) => {
    const response = await api.delete(`/projects/${id}`);
    return response.data;
  },
  assignProject: async (userId: number, projectId: number) => {
    const response = await api.post('/projects/assign', { userId, projectId });
    return response.data;
  },
  unassignProject: async (userId: number, projectId: number) => {
    const response = await api.delete('/projects/unassign', { data: { userId, projectId } });
    return response.data;
  },
  getProjectEmployees: async (projectId: number) => {
    const response = await api.get(`/projects/${projectId}/employees`);
    return response.data;
  },
};

export const timesheetService = {
  getMyTimesheets: async () => {
    const response = await api.get('/timesheets/my');
    return response.data;
  },
  getTeamTimesheets: async () => {
    const response = await api.get('/timesheets/team');
    return response.data;
  },
  getAllTimesheets: async () => {
    const response = await api.get('/timesheets/all');
    return response.data;
  },
  getPendingTimesheets: async () => {
    const response = await api.get('/timesheets/pending');
    return response.data;
  },
  createTimesheet: async (timesheetData: any) => {
    const response = await api.post('/timesheets', timesheetData);
    return response.data;
  },
  updateTimesheet: async (id: number, timesheetData: any) => {
    const response = await api.put(`/timesheets/${id}`, timesheetData);
    return response.data;
  },
  submitTimesheet: async (id: number) => {
    const response = await api.post(`/timesheets/${id}/submit`);
    return response.data;
  },
  approveTimesheet: async (id: number) => {
    const response = await api.post(`/timesheets/${id}/approve`);
    return response.data;
  },
  rejectTimesheet: async (id: number, reason: string) => {
    const response = await api.post(`/timesheets/${id}/reject`, { rejectionReason: reason });
    return response.data;
  },
  unlockTimesheet: async (id: number) => {
    const response = await api.post(`/timesheets/${id}/unlock`);
    return response.data;
  },
  deleteTimesheet: async (id: number) => {
    const response = await api.delete(`/timesheets/${id}`);
    return response.data;
  },
  getMonthlyReport: async (month: number, year: number, userId?: number, projectId?: number) => {
    const params = new URLSearchParams({ month: month.toString(), year: year.toString() });
    if (userId) params.append('userId', userId.toString());
    if (projectId) params.append('projectId', projectId.toString());
    const response = await api.get(`/timesheets/report/monthly?${params.toString()}`);
    return response.data;
  },
  getWeeklyReport: async (weekStartDate: string, userId?: number, projectId?: number) => {
    const params = new URLSearchParams({ weekStartDate });
    if (userId) params.append('userId', userId.toString());
    if (projectId) params.append('projectId', projectId.toString());
    const response = await api.get(`/timesheets/report/weekly?${params.toString()}`);
    return response.data;
  },
  generateShareLink: async (userId: number, month: number, year: number, expiryDays?: number) => {
    const response = await api.post('/timesheets/generate-share-link', { userId, month, year, expiryDays });
    return response.data;
  },
};

export const publicReportService = {
  getPublicReport: async (token: string) => {
    const response = await axios.get(`${API_URL}/PublicReports/${token}`);
    return response.data;
  },
};

export const attendanceService = {
  getMyAttendance: async (startDate?: string, endDate?: string) => {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    const response = await api.get(`/attendance/my?${params.toString()}`);
    return response.data;
  },
  getAllAttendance: async (startDate?: string, endDate?: string) => {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    const response = await api.get(`/attendance/all?${params.toString()}`);
    return response.data;
  },
  // Admin: one specific employee's full attendance history — unlike getAllAttendance, this
  // works for deactivated employees too (backs the employee detail drill-down).
  getUserAttendance: async (userId: number, startDate?: string, endDate?: string) => {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    const response = await api.get(`/attendance/user/${userId}?${params.toString()}`);
    return response.data;
  },
  markAttendance: async (checkInTime: string, status: string, remarks?: string) => {
    const response = await api.post('/attendance/mark', { checkInTime, status, remarks });
    return response.data;
  },
  checkOut: async (id: number, checkOutTime: string) => {
    const response = await api.put(`/attendance/${id}/checkout`, { checkOutTime });
    return response.data;
  },
  deleteAttendance: async (id: number) => {
    const response = await api.delete(`/attendance/${id}`);
    return response.data;
  },
};

export const leaveRequestService = {
  getMyLeaveRequests: async () => {
    const response = await api.get('/leaverequests/my');
    return response.data;
  },
  getPendingLeaveRequests: async () => {
    const response = await api.get('/leaverequests/pending');
    return response.data;
  },
  getAllLeaveRequests: async () => {
    const response = await api.get('/leaverequests/all');
    return response.data;
  },
  createLeaveRequest: async (leaveData: any) => {
    const response = await api.post('/leaverequests', leaveData);
    return response.data;
  },
  approveLeaveRequest: async (id: number) => {
    const response = await api.post(`/leaverequests/${id}/approve`);
    return response.data;
  },
  rejectLeaveRequest: async (id: number, rejectionReason: string) => {
    const response = await api.post(`/leaverequests/${id}/reject`, { rejectionReason });
    return response.data;
  },
  revokeLeaveRequest: async (id: number) => {
    const response = await api.post(`/leaverequests/${id}/revoke`);
    return response.data;
  },
  cancelLeaveRequest: async (id: number) => {
    const response = await api.post(`/leaverequests/${id}/cancel`);
    return response.data;
  },
};

export const roleService = {
  getRoles: async () => {
    const response = await api.get('/roles');
    return response.data;
  },
  getPermissions: async () => {
    const response = await api.get('/roles/permissions');
    return response.data;
  },
  createRole: async (roleData: any) => {
    const response = await api.post('/roles', roleData);
    return response.data;
  },
  assignRole: async (userId: number, roleId: number, managerId?: number) => {
    const response = await api.post('/roles/assign', { userId, roleId, managerId });
    return response.data;
  },
  getUserTeam: async (userId: number) => {
    const response = await api.get(`/roles/user/${userId}/team`);
    return response.data;
  },
};

export const holidayService = {
  getHolidays: async () => {
    const response = await api.get('/holidays');
    return response.data;
  },
  createHoliday: async (holidayData: any) => {
    const response = await api.post('/holidays', holidayData);
    return response.data;
  },
  updateHoliday: async (id: number, holidayData: any) => {
    const response = await api.put(`/holidays/${id}`, holidayData);
    return response.data;
  },
  deleteHoliday: async (id: number) => {
    const response = await api.delete(`/holidays/${id}`);
    return response.data;
  },
};

export const goalService = {
  getGoals: async () => {
    const response = await api.get('/goals');
    return response.data;
  },
  createGoal: async (goalData: any) => {
    const response = await api.post('/goals', goalData);
    return response.data;
  },
  updateGoal: async (id: number, goalData: any) => {
    const response = await api.put(`/goals/${id}`, goalData);
    return response.data;
  },
  deleteGoal: async (id: number) => {
    const response = await api.delete(`/goals/${id}`);
    return response.data;
  },
  createHalfYearlyGoals: async () => {
    const response = await api.post('/goals/half-yearly');
    return response.data;
  },
};

export const salaryService = {
  getMonthlySalaryReport: async (month: number, year: number, userId?: number) => {
    const params = new URLSearchParams({ month: month.toString(), year: year.toString() });
    if (userId) params.append('userId', userId.toString());
    const response = await api.get(`/salary/monthly-report?${params.toString()}`);
    return response.data;
  },
  saveMonthlySalary: async (month: number, year: number, userId?: number) => {
    const params = new URLSearchParams({ month: month.toString(), year: year.toString() });
    if (userId) params.append('userId', userId.toString());
    const response = await api.post(`/salary/save-monthly-salary?${params.toString()}`);
    return response.data;
  },
  getPaymentHistory: async (userId?: number, month?: number, year?: number) => {
    const params = new URLSearchParams();
    if (userId) params.append('userId', userId.toString());
    if (month) params.append('month', month.toString());
    if (year) params.append('year', year.toString());
    const response = await api.get(`/salary/payment-history?${params.toString()}`);
    return response.data;
  },
  markAsPaid: async (id: number) => {
    const response = await api.post(`/salary/${id}/mark-paid`);
    return response.data;
  },
  // Employee self-service
  getMyPayslips: async (year?: number) => {
    const params = new URLSearchParams();
    if (year) params.append('year', year.toString());
    const response = await api.get(`/salary/my-payslips?${params.toString()}`);
    return response.data;
  },
  getMyCTC: async (year: number) => {
    const response = await api.get(`/salary/my-ctc?year=${year}`);
    return response.data;
  },
};

export const leaveBalanceService = {
  getMyBalance: async () => {
    const response = await api.get('/leavebalance/my-balance');
    return response.data;
  },
  getAllBalances: async () => {
    const response = await api.get('/leavebalance/all-balances');
    return response.data;
  },
  getTransactions: async () => {
    const response = await api.get('/leavebalance/transactions');
    return response.data;
  },
  adjustBalance: async (userId: number, leaveType: string, amount: number, remarks?: string) => {
    const response = await api.post('/leavebalance/adjust-balance', { userId, leaveType, amount, remarks });
    return response.data;
  },
  revokeLastCredit: async (userId: number, leaveType: string) => {
    const response = await api.post('/leavebalance/revoke-last-credit', { userId, leaveType });
    return response.data;
  },
  creditMonthly: async () => {
    const response = await api.post('/leavebalance/credit-monthly');
    return response.data;
  },
};

export const onboardingService = {
  getMy: async () => {
    const response = await api.get('/onboarding/my');
    return response.data;
  },
  getAll: async () => {
    const response = await api.get('/onboarding');
    return response.data;
  },
  getById: async (id: number) => {
    const response = await api.get(`/onboarding/${id}`);
    return response.data;
  },
  save: async (data: any) => {
    const response = await api.post('/onboarding/save', data);
    return response.data;
  },
  submit: async () => {
    const response = await api.post('/onboarding/submit');
    return response.data;
  },
  review: async (id: number, data: { action: string; changeRequestReason?: string }) => {
    const response = await api.post(`/onboarding/${id}/review`, data);
    return response.data;
  },
  saveOfficeInfo: async (id: number, data: any) => {
    const response = await api.put(`/onboarding/${id}/office-info`, data);
    return response.data;
  },
  uploadOfferLetter: async (id: number, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/onboarding/${id}/offer-letter`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  uploadDocument: async (id: number, documentType: string, file: File) => {
    const formData = new FormData();
    formData.append('documentType', documentType);
    formData.append('file', file);
    const response = await api.post(`/onboarding/${id}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  deleteDocument: async (id: number, docId: number) => {
    const response = await api.delete(`/onboarding/${id}/documents/${docId}`);
    return response.data;
  },
};

export const interviewFeedbackService = {
  getAll: async (params?: { type?: string; search?: string; decision?: string }) => {
    const query = new URLSearchParams();
    if (params?.type) query.append('type', params.type);
    if (params?.search) query.append('search', params.search);
    if (params?.decision) query.append('decision', params.decision);
    const response = await api.get(`/interviewfeedback?${query.toString()}`);
    return response.data;
  },
  getById: async (id: number) => {
    const response = await api.get(`/interviewfeedback/${id}`);
    return response.data;
  },
  create: async (data: any) => {
    const response = await api.post('/interviewfeedback', data);
    return response.data;
  },
  update: async (id: number, data: any) => {
    const response = await api.put(`/interviewfeedback/${id}`, data);
    return response.data;
  },
  delete: async (id: number) => {
    const response = await api.delete(`/interviewfeedback/${id}`);
    return response.data;
  },
};

export const attendanceRegularizationService = {
  getMy: async () => {
    const response = await api.get('/attendanceregularization/my');
    return response.data;
  },
  getPending: async () => {
    const response = await api.get('/attendanceregularization/pending');
    return response.data;
  },
  getAll: async (status?: string) => {
    const query = status ? `?status=${status}` : '';
    const response = await api.get(`/attendanceregularization/all${query}`);
    return response.data;
  },
  submit: async (data: {
    requestedDate: string;
    requestedCheckIn: string;
    requestedCheckOut?: string;
    requestedStatus: string;
    reason: string;
  }) => {
    const response = await api.post('/attendanceregularization', data);
    return response.data;
  },
  approve: async (id: number) => {
    const response = await api.post(`/attendanceregularization/${id}/approve`);
    return response.data;
  },
  reject: async (id: number, rejectionReason: string) => {
    const response = await api.post(`/attendanceregularization/${id}/reject`, { rejectionReason });
    return response.data;
  },
};

export const googleMeetService = {
  // Employee
  getToday: async () => {
    const response = await api.get('/googlemeet/today');
    return response.data;
  },
  getTodayAccess: async () => {
    const response = await api.get('/googlemeet/today/access');
    return response.data;
  },

  // Admin — connection lifecycle
  connect: async () => {
    const response = await api.post('/googlemeet/connect');
    return response.data;
  },
  getStatus: async () => {
    const response = await api.get('/googlemeet/status');
    return response.data;
  },
  disconnect: async () => {
    const response = await api.post('/googlemeet/disconnect');
    return response.data;
  },

  // Admin — daily meeting
  createDaily: async () => {
    const response = await api.post('/googlemeet/daily/create');
    return response.data;
  },
  getDaily: async () => {
    const response = await api.get('/googlemeet/daily');
    return response.data;
  },

  // Admin — reporting
  getParticipants: async (meetingId: number) => {
    const response = await api.get(`/googlemeet/${meetingId}/participants`);
    return response.data;
  },
  getSessions: async (meetingId: number) => {
    const response = await api.get(`/googlemeet/${meetingId}/sessions`);
    return response.data;
  },
  getReport: async (meetingId: number) => {
    const response = await api.get(`/googlemeet/${meetingId}/report`);
    return response.data;
  },
  resolveAmbiguousParticipant: async (displayName: string, googleUserId: string | null, employeeId: number) => {
    const response = await api.post('/googlemeet/participants/resolve', { displayName, googleUserId, employeeId });
    return response.data;
  },
};

// LiveKit "virtual presence" integration — independent feature from googleMeetService above,
// gated server-side behind LiveKit:Enabled (see LIVEKIT_SETUP.md). Same attendance-gated access
// pattern as Google Meet (getTodayAccess never returns a token unless the server-side check
// actually passed), but returns a join token for the embedded LiveKit React UI instead of an
// external meeting URL to open.
// Rooms are project-scoped (each Project has its own daily virtual-office room, joinable only by
// employees actively assigned to it) — every call below except getMyProjectsAccess/getReport
// takes a projectId.
export const liveKitService = {
  // Employee
  getMyProjectsAccess: async () => {
    const response = await api.get('/livekit/myprojects/access');
    return response.data;
  },
  getToday: async (projectId: number) => {
    const response = await api.get('/livekit/today', { params: { projectId } });
    return response.data;
  },
  getTodayAccess: async (projectId: number) => {
    const response = await api.get('/livekit/today/access', { params: { projectId } });
    return response.data;
  },
  // My own join/leave/duration history, date-wise. fromDate/toDate as 'yyyy-MM-dd'; defaults to
  // the last 30 days server-side when omitted.
  getMyHistory: async (fromDate?: string, toDate?: string, projectId?: number) => {
    const response = await api.get('/livekit/myhistory', { params: { fromDate, toDate, projectId } });
    return response.data;
  },
  // Drill-down: the individual join/leave sessions behind one of my history rows' totals.
  getMySessions: async (presenceDayId: number) => {
    const response = await api.get(`/livekit/${presenceDayId}/mysessions`);
    return response.data;
  },

  // Admin — daily room
  createDaily: async (projectId: number) => {
    const response = await api.post('/livekit/daily/create', null, { params: { projectId } });
    return response.data;
  },
  getDaily: async (projectId: number, date?: string) => {
    const response = await api.get('/livekit/daily', { params: { projectId, date } });
    return response.data;
  },

  // Admin — reporting
  getReport: async (presenceDayId: number) => {
    const response = await api.get(`/livekit/${presenceDayId}/report`);
    return response.data;
  },
  // Drill-down: one employee's individual sessions within a day's report.
  getSessions: async (presenceDayId: number, employeeId: number) => {
    const response = await api.get(`/livekit/${presenceDayId}/sessions`, { params: { employeeId } });
    return response.data;
  },
};

// Temporary meeting rooms — employee-created, project-agnostic LiveKit rooms joined by a short
// shareable code, so an employee with no project room available (or a quick side-huddle) doesn't
// lose virtual-office presence. Separate feature/table from liveKitService's project-scoped rooms
// above — see LIVEKIT_SETUP.md "Temporary meeting rooms".
export const liveKitTempRoomService = {
  // Employee
  create: async (title?: string) => {
    const response = await api.post('/livekittemprooms/create', { title });
    return response.data;
  },
  joinByCode: async (roomCode: string) => {
    const response = await api.post('/livekittemprooms/join', { roomCode });
    return response.data;
  },
  end: async (id: number) => {
    const response = await api.post(`/livekittemprooms/${id}/end`);
    return response.data;
  },
  // Restores UI state after a page reload — non-null means the caller should re-joinByCode() with
  // the returned roomCode to get a fresh token.
  getMyActive: async () => {
    const response = await api.get('/livekittemprooms/myactive');
    return response.data;
  },
  getMySessions: async (id: number) => {
    const response = await api.get(`/livekittemprooms/${id}/mysessions`);
    return response.data;
  },

  // Admin
  getActiveRooms: async () => {
    const response = await api.get('/livekittemprooms/active');
    return response.data;
  },
  getParticipants: async (id: number) => {
    const response = await api.get(`/livekittemprooms/${id}/participants`);
    return response.data;
  },
  getSessions: async (id: number, employeeId: number) => {
    const response = await api.get(`/livekittemprooms/${id}/sessions`, { params: { employeeId } });
    return response.data;
  },
};

// Admin-only runtime on/off switches for integrations (GoogleMeet, LiveKit) that also ship a
// compiled-in default — flipping one here takes effect immediately, no redeploy.
export const featureToggleService = {
  getAll: async () => {
    const response = await api.get('/featuretoggles');
    return response.data as { featureKey: string; enabled: boolean; updatedAt: string | null }[];
  },
  setEnabled: async (featureKey: string, enabled: boolean) => {
    const response = await api.post(`/featuretoggles/${featureKey}`, { enabled });
    return response.data;
  },
};

// Candidate interview scheduling (Admin/Manager) — creates the DB row, LiveKit room and sends
// the candidate invite email in one call. See InterviewPanelController for a panelist's own view,
// and publicInterviewService for the unauthenticated candidate-facing join.
export const interviewScheduleService = {
  getAll: async (params?: { status?: string; search?: string; hiringType?: string }) => {
    const response = await api.get('/interviewschedule', { params });
    return response.data;
  },
  getById: async (id: number) => {
    const response = await api.get(`/interviewschedule/${id}`);
    return response.data;
  },
  create: async (data: {
    candidateName: string;
    candidateEmail: string;
    candidatePhone?: string;
    position: string;
    hiringType: string; // Intern / FullTime / PartTime / Contract
    scheduledAt: string; // "YYYY-MM-DDTHH:mm" IST wall-clock, from a datetime-local input
    durationMinutes: number;
    notes?: string;
    panelUserIds: number[];
  }) => {
    const response = await api.post('/interviewschedule', data);
    return response.data;
  },
  reschedule: async (id: number, data: { scheduledAt: string; durationMinutes?: number; notes?: string; panelUserIds?: number[] }) => {
    const response = await api.post(`/interviewschedule/${id}/reschedule`, data);
    return response.data;
  },
  cancel: async (id: number, reason?: string) => {
    const response = await api.post(`/interviewschedule/${id}/cancel`, { reason });
    return response.data;
  },
  // Retries the candidate invite email — for when the schedule/reschedule response came back
  // with inviteEmailSent: false (Email Settings wasn't configured/working at the time).
  resendInvite: async (id: number) => {
    const response = await api.post(`/interviewschedule/${id}/resend-invite`);
    return response.data;
  },
  uploadResume: async (id: number, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/interviewschedule/${id}/resume`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
};

// Any employee's own "interviews I'm on the panel for" — visible regardless of role, see
// InterviewPanelController.
export const interviewPanelService = {
  getMine: async () => {
    const response = await api.get('/interviewpanel/mine');
    return response.data;
  },
  join: async (id: number) => {
    const response = await api.post(`/interviewpanel/${id}/join`);
    return response.data as { token: string; serverUrl: string };
  },
};

// Unauthenticated candidate-facing endpoints — the token is opaque and embedded in the emailed
// link (/interview/:token), never the numeric interview id. No Authorization header is required
// (none will be attached unless a logged-in HR user previews the same link, which is harmless).
export const publicInterviewService = {
  getDetail: async (token: string) => {
    const response = await api.get(`/public/interviews/${token}`);
    return response.data;
  },
  join: async (token: string) => {
    const response = await api.post(`/public/interviews/${token}/join`);
    return response.data as { canJoin: boolean; token?: string; serverUrl?: string; reason?: string };
  },
};

// Admin-configurable outbound SMTP mailbox (hr@mqbittechnologies.com) — settings including the
// password live in the DB (see EmailSettingsController), not appsettings.json. GET never returns
// the raw password, only `hasPassword`; PUT's `password` field is optional — leave it blank to
// keep the currently-stored one.
export const emailSettingsService = {
  get: async () => {
    const response = await api.get('/emailsettings');
    return response.data as {
      enabled: boolean; host: string; port: number; useStartTls: boolean; username: string;
      hasPassword: boolean; fromAddress: string; fromName: string; updatedAt: string | null;
    };
  },
  update: async (data: {
    enabled: boolean; host: string; port: number; useStartTls: boolean; username: string;
    password?: string; fromAddress: string; fromName: string;
  }) => {
    const response = await api.put('/emailsettings', data);
    return response.data;
  },
};

// Internship lifecycle — mentor check-ins, final evaluation, HR decision and certificates.
// Every mutating call returns the refreshed InternshipDetail, so the UI just swaps it in.
export const internshipService = {
  getAll: async (params?: { status?: string; endingWithinDays?: number }) => {
    const response = await api.get('/internships', { params });
    return response.data;
  },
  getDashboard: async () => {
    const response = await api.get('/internships/dashboard');
    return response.data;
  },
  getById: async (id: number) => {
    const response = await api.get(`/internships/${id}`);
    return response.data;
  },
  // Resolves to null when the caller has never been an intern (API returns 404).
  getMine: async () => {
    try {
      const response = await api.get('/internships/me');
      return response.data;
    } catch (err: any) {
      if (err.response?.status === 404) return null;
      throw err;
    }
  },
  create: async (data: any) => {
    const response = await api.post('/internships', data);
    return response.data;
  },
  update: async (id: number, data: any) => {
    const response = await api.put(`/internships/${id}`, data);
    return response.data;
  },
  submitSelfAssessment: async (id: number, reviewId: number, selfAssessment: string) => {
    const response = await api.post(`/internships/${id}/reviews/${reviewId}/self`, { selfAssessment });
    return response.data;
  },
  submitMentorReview: async (id: number, reviewId: number, data: any) => {
    const response = await api.post(`/internships/${id}/reviews/${reviewId}/mentor`, data);
    return response.data;
  },
  shareReview: async (id: number, reviewId: number) => {
    const response = await api.post(`/internships/${id}/reviews/${reviewId}/share`);
    return response.data;
  },
  submitRecommendation: async (id: number, data: { recommendation: string; reason: string }) => {
    const response = await api.post(`/internships/${id}/recommendation`, data);
    return response.data;
  },
  submitDecision: async (id: number, data: any) => {
    const response = await api.post(`/internships/${id}/decision`, data);
    return response.data;
  },
  issueCertificate: async (id: number) => {
    const response = await api.post(`/internships/${id}/certificates`);
    return response.data;
  },
  revokeCertificate: async (id: number, certificateId: number, reason: string) => {
    const response = await api.post(`/internships/${id}/certificates/${certificateId}/revoke`, { reason });
    return response.data;
  },
  resendCertificate: async (id: number, certificateId: number) => {
    const response = await api.post(`/internships/${id}/certificates/${certificateId}/resend`);
    return response.data;
  },
  enableShare: async (id: number) => {
    const response = await api.post(`/internships/${id}/share`);
    return response.data;
  },
  disableShare: async (id: number) => {
    const response = await api.delete(`/internships/${id}/share`);
    return response.data;
  },
};

export const publicInternshipService = {
  get: async (token: string) => {
    const response = await api.get(`/public/internships/${token}`);
    return response.data;
  },
};

// Unauthenticated certificate verification — the opaque token comes from the link/QR printed
// on the certificate (/certificate/:token), never a numeric id.
export const publicCertificateService = {
  verify: async (token: string) => {
    const response = await api.get(`/public/certificates/${token}`);
    return response.data;
  },
};

export default api;
